import React, {
  useEffect, forwardRef, useImperativeHandle, useRef, useMemo,
} from 'react';
import { Box, useToken } from '@gluestack-ui/themed';
import ReactQuill from 'react-quill';

import '../../assets/css/quill.snow.css';
import { useDialog } from '../context/DialogContext';

// Register custom icons for undo / redo / history so Quill renders buttons
const quillIcons = ReactQuill.Quill.import('ui/icons');
quillIcons.undo = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10"></polygon>
    <path class="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"></path>
  </svg>`;
quillIcons.redo = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10"></polygon>
    <path class="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"></path>
  </svg>`;
// History icon: Adapted from Lucide 'history' (scaled to fit nicely with other icons)
quillIcons.history = `<svg viewbox="0 0 18 18">
    <g transform="translate(1.2, 1.2) scale(0.65)">
      <path class="ql-stroke" d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" style="stroke-width: 3px; fill: none; stroke-linecap: round; stroke-linejoin: round;"></path>
      <path class="ql-stroke" d="M3 3v5h5" style="stroke-width: 3px; fill: none; stroke-linecap: round; stroke-linejoin: round;"></path>
      <path class="ql-stroke" d="M12 7v5l4 2" style="stroke-width: 2px; fill: none; stroke-linecap: round; stroke-linejoin: round;"></path>
    </g>
  </svg>`;

/**
 * WebQuill - Rich text editor wrapper around ReactQuill
 *
 * @param {boolean} compact - Controls toolbar feature set. When true, shows minimal toolbar
 *                           (header, bold, undo/redo). When false, shows full toolbar with
 *                           lists, code-block, etc. Defaults to true.
 * @param {boolean} hideToolbar - Completely hides the toolbar. Defaults to false.
 * @param {boolean} borderless - Removes all borders and adjusts padding for seamless
 *                              integration into parent containers. Best used with compact mode
 *                              for inline editing contexts. Defaults to false.
 */
const WebQuill = forwardRef((props, ref) => {
  const {
    initialHtml = '',
    onHtmlChange = () => {},
    onShowHistory,
    showHistoryControl = false,
    style: propStyle = {},
    readonly = false,
    placeholder = '',
    compact = true,
    hideToolbar = false,
    borderless = false,
    value,
    ...restProps
  } = props;

  const { showDialog } = useDialog();
  const borderLight = useToken('colors', 'borderLight');

  // Keep a stable ref across re-renders so toolbar handlers always access the live editor instance
  const quillRef = useRef(null);

  // Expose methods to the parent component
  useImperativeHandle(ref, () => ({
    getHtml: async () => {
      if (!quillRef.current) return '';
      // quillRef.current is the ReactQuill component
      // .value is the controlled value or the internal state
      return quillRef.current.value || '';
    },
    setHtml: async (html) => {
      if (!quillRef.current) throw new Error('WebQuill not mounted');
      const editor = quillRef.current.getEditor();
      if (!editor || !editor.clipboard) throw new Error('Quill editor or clipboard not initialized');
      await editor.clipboard.dangerouslyPasteHTML(html);
    },
    blur: async () => {
      if (!quillRef.current) return;
      const editor = quillRef.current.getEditor();
      if (editor) await editor.blur();
    },
    disable: async () => {
      if (!quillRef.current) return;
      const editor = quillRef.current.getEditor();
      if (editor) await editor.disable();
    },
    enable: async () => {
      if (!quillRef.current) return;
      const editor = quillRef.current.getEditor();
      if (editor) await editor.enable();
    },
    getEditor: () => quillRef.current?.getEditor(),
    getSelection: () => quillRef.current?.getEditor()?.getSelection(),
    setSelection: (range) => quillRef.current?.getEditor()?.setSelection(range),
  }));

  /*
  Custom toolbar options to match the native editor
  about "matchVisual: false":
  to import innerHTML it uses clipboard API, this tries to match the visual of
  copy/pasted material, but this would lead to inserting newlines where there is spacing
  (e.g. after h1, before ol, ul, etc.)
  */

  const showNoImageDialog = React.useCallback(() => {
    showDialog({
      title: 'Style Guide Note',
      message:
        "Please don't include images in your text. Upload figures, tables and diagrams as attachments. Make sure to reference any attachments in your text.",
      type: 'info',
      buttons: [
        {
          text: 'OK',
        },
      ],
    });
  }, [showDialog]);

  useEffect(() => {
    // code to handle paste as plain text while detecting images
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();

      // Import Delta class
      const Delta = ReactQuill.Quill.import('delta');

      // Handle paste event directly to check for images first
      editor.root.addEventListener('paste', (e) => {
        const hasHtml = e.clipboardData.types.includes('text/html');

        if (hasHtml) {
          const html = e.clipboardData.getData('text/html');
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;

          // Check if HTML contains images
          const hasImagesInHtml = tempDiv.querySelectorAll('img').length > 0;

          if (hasImagesInHtml) {
            showNoImageDialog();
          }
        }

        // Continue with Quill's normal paste handling
        // Our clipboard matcher will strip formatting
      });

      // Process the pasted content to strip formatting but preserve structure
      editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        // Check if this is an image node
        if (node.tagName === 'IMG') {
          return new Delta().insert(''); // Remove images
        }

        // Allowed tags - only these will preserve their structure
        const allowedTags = ['P', 'DIV', 'H1', 'B', 'STRONG', 'OL', 'UL', 'LI'];
        const isAllowedTag = allowedTags.includes(node.tagName);

        if (delta.ops) {
          // Filter out any image inserts
          const filteredOps = delta.ops.filter((op) => !(op.insert && typeof op.insert === 'object' && op.insert.image));

          // Strip all formatting attributes except for allowed tags
          const cleanedOps = filteredOps.map((op) => {
            if (op.attributes) {
              // Create new attributes object with only allowed formatting
              const newAttributes = {};

              // For allowed tags, preserve only structure-related attributes
              if (isAllowedTag) {
                if (node.tagName === 'H1') {
                  newAttributes.header = 1;
                }
                if (op.attributes.bold || node.tagName === 'B' || node.tagName === 'STRONG') {
                  newAttributes.bold = true;
                }
                if (node.tagName === 'LI') {
                  // Preserve list type from parent
                  if (op.attributes.list) {
                    newAttributes.list = op.attributes.list;
                  }
                }
              }

              // Remove all color, background, font, size, and other styling
              // Only return the cleaned attributes
              return {
                insert: op.insert,
                attributes: Object.keys(newAttributes).length > 0 ? newAttributes : undefined,
              };
            }
            return op;
          });

          // Remove undefined attributes to clean up the delta
          const finalOps = cleanedOps.map((op) => {
            if (op.attributes === undefined) {
              return { insert: op.insert };
            }
            return op;
          });

          if (finalOps.length !== delta.ops.length || JSON.stringify(finalOps) !== JSON.stringify(delta.ops)) {
            return new Delta(finalOps);
          }

          return new Delta(finalOps);
        }
        // Fallback to basic text extraction
        const text = node.innerText || '';
        return new Delta().insert(text);
      });

      // Still handle image drops separately
      editor.root.addEventListener('drop', (e) => {
        const hasImageTypes = Array.from(e.dataTransfer.types).some(
          (type) => type === 'Files' || type.startsWith('image/'),
        );

        if (hasImageTypes) {
          showNoImageDialog();
          e.preventDefault();
        }
      });
    }
  }, [showNoImageDialog]);

  // toolbars

  // Keep a ref to the onShowHistory callback so the toolbar handler can access it
  const onShowHistoryRef = useRef(onShowHistory);
  useEffect(() => {
    onShowHistoryRef.current = onShowHistory;
  }, [onShowHistory]);

  const hasHistoryCallback = !!onShowHistoryRef.current;

  const activeQuillModules = useMemo(() => {
    const baseModules = {
      clipboard: { matchVisual: false },
      history: { delay: 1000, maxStack: 500, userOnly: true },
    };

    if (hideToolbar) {
      return { ...baseModules, toolbar: false };
    }

    const showHistory = hasHistoryCallback && showHistoryControl;
    const historyGroup = showHistory ? [['history']] : [];

    const toolbarContainer = compact
      ? [[{ header: 1 }], ['bold'], ['undo', 'redo'], ...historyGroup]
      : [
        [{ header: 1 }, 'bold'],
        ['undo', 'redo'],
        ...historyGroup,
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ background: ['#ffffff', '#a9dbd0'] }, 'code-block'],
      ];

    return {
      ...baseModules,
      toolbar: {
        container: toolbarContainer,
        handlers: {
          undo: () => quillRef.current?.getEditor().history.undo(),
          redo: () => quillRef.current?.getEditor().history.redo(),
          history: () => onShowHistoryRef.current?.(),
        },
      },
    };
  }, [compact, showHistoryControl, hideToolbar, hasHistoryCallback]);

  const modulesKey = useMemo(() => {
    if (hideToolbar) return 'quill-no-toolbar';
    const showHistory = hasHistoryCallback && showHistoryControl;
    return `quill-${compact}-${showHistory}`;
  }, [compact, showHistoryControl, hideToolbar, hasHistoryCallback]);

  const handleChange = React.useCallback(
    (nextHtml, _delta, source) => {
      try {
        if (typeof onHtmlChange === 'function') {
          onHtmlChange(nextHtml, { source });
        }
      } catch {
        // Ignore change errors
      }
    },
    [onHtmlChange],
  );

  // Generate unique ID for scoped borderless styles
  const borderlessId = React.useId();

  // Borderless mode CSS overrides - scoped to this instance only
  // Padding strategy:
  // - hideToolbar + compact: minimal padding (6px/8px)
  // - hideToolbar only: slightly more padding (8px/12px)
  // - with toolbar + compact: moderate padding (12px/12px)
  // - with toolbar (full): more padding for visual separation (12px/16px)
  const borderlessStyles = borderless
    ? `
      #${CSS.escape(borderlessId)} .ql-toolbar.ql-snow {
        border: none !important;
        ${hideToolbar ? '' : `border-bottom: 1px solid ${borderLight} !important;`}
        padding: ${compact ? '6px 12px' : '8px 16px'} !important;
        background: transparent !important;
      }
      #${CSS.escape(borderlessId)} .ql-container.ql-snow {
        border: none !important;
        background: transparent !important;
      }
      #${CSS.escape(borderlessId)} .ql-editor {
        padding: ${hideToolbar
    ? (compact ? '6px 8px' : '8px 12px')
    : (compact ? '8px 8px 32px 8px' : '12px 12px 48px 12px')
} !important;
        font-size: 16px !important;
        line-height: 1.6 !important;
      }
      #${CSS.escape(borderlessId)} .ql-editor.ql-blank::before {
        left: ${compact ? '8px' : '12px'} !important;
        top: ${hideToolbar ? (compact ? '6px' : '8px') : (compact ? '8px' : '12px')} !important;
        font-style: normal !important;
        color: #9ca3af !important;
      }
      #${CSS.escape(borderlessId)} .ql-editor.ql-blank:focus::before {
        display: none !important;
      }
      /* Subtle hover effect for toolbar buttons */
      #${CSS.escape(borderlessId)} .ql-snow.ql-toolbar button:hover,
      #${CSS.escape(borderlessId)} .ql-snow .ql-toolbar button:hover {
        color: var(--primary-500) !important;
        background-color: #f9fafb !important;
        border-radius: 4px;
      }
    `
    : '';

  return (
    <Box h="100%" id={borderless ? borderlessId : undefined}>
      {borderless && <style>{borderlessStyles}</style>}
      <ReactQuill
        {...restProps}
        key={modulesKey}
        ref={quillRef}
        {...(value === undefined || value === null ? { defaultValue: initialHtml } : { value })}
        onChange={handleChange}
        modules={activeQuillModules}
        theme="snow"
        style={{
          ...propStyle,
        }}
        readOnly={readonly}
        placeholder={placeholder}
      />
    </Box>
  );
});

export default WebQuill;
